import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Plus, Loader2, Calendar, X, MapPin, Users, Edit2, Trash2, Ticket, ExternalLink, ClipboardCheck, BarChart3, Theater } from 'lucide-react';
import { toast } from 'sonner';
import PlanSalleEditor from '../components/PlanSalleEditor';
import EmargementModal from '../components/EmargementModal';
import BilletterieStatsModal from '../components/BilletterieStatsModal';
import ImageUploader from '../components/ImageUploader';
import { mediaUrl } from '@/lib/media';

export default function AssoEvenements() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [emargement, setEmargement] = useState(null);
  const [statsFor, setStatsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/evenements'); setList(data); }
    catch { toast.error('Erreur de chargement'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (e) => {
    if (!window.confirm(`Supprimer "${e.nom}" ? Tous les billets liés seront perdus.`)) return;
    try {
      await api.delete(`/evenements/${e.id}`);
      toast.success('Événement supprimé');
      load();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
  };

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="asso-evenements">
      <div className="flex items-center justify-between mb-8 slide-up gap-3 flex-wrap">
        <div>
          <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Événements</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">
            {list.length} événement{list.length > 1 ? 's' : ''}
          </h1>
        </div>
        <button onClick={() => setEditing({})} className="btn-primary flex items-center gap-2" data-testid="add-evenement-btn">
          <Plus className="w-4 h-4" /> Créer un événement
        </button>
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : list.length === 0 ? (
        <div className="card py-16 text-center text-[#475569]" data-testid="empty-state">
          <Calendar className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <p className="font-medium mb-1">Aucun événement pour l'instant.</p>
          <p className="text-xs">Cliquez sur "Créer" pour ajouter votre premier événement.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="evenements-grid">
          {list.map((e) => {
            const past = new Date(e.date_debut) < new Date();
            return (
              <div key={e.id} className="card card-hover relative" data-testid={`evt-card-${e.id}`}>
                {e.image_url && (
                  <img src={mediaUrl(e.image_url)} alt={e.nom} className="w-full h-32 object-cover rounded-xl mb-3" data-testid={`evt-image-${e.id}`} />
                )}
                <div className="flex items-center justify-between mb-3">
                  <span className={`badge ${past ? 'badge-neutral' : 'badge-primary'} capitalize`}>{e.type || 'autre'}</span>
                  <div className="flex items-center gap-1.5">
                    {(e.mode_spectacle || e.plan_salle?.mode === 'spectacle') && (
                      <span className="badge badge-info inline-flex items-center gap-1" title="Mode Spectacle (sièges numérotés)"><Theater className="w-3 h-3" /> Spectacle</span>
                    )}
                    {e.billetterie && <span className="badge badge-info">{e.nb_billets_vendus || 0} billet{(e.nb_billets_vendus || 0) > 1 ? 's' : ''}</span>}
                  </div>
                </div>
                <h3 className="font-display text-lg font-bold text-[#0F172A] mb-1">{e.nom}</h3>
                <p className="text-xs text-[#475569] line-clamp-2 mb-4 min-h-[2rem]">{e.description || '—'}</p>
                <div className="space-y-1.5 text-xs text-[#475569] mb-3">
                  <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#94A3B8]" /> {new Date(e.date_debut).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                  {e.lieu && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#94A3B8]" /> {e.lieu}</div>}
                  {e.capacite && <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5 text-[#94A3B8]" /> {e.capacite} places</div>}
                </div>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-[#F1F5F9]">
                  <button
                    onClick={() => setEmargement(e.id)}
                    className="btn-ghost !py-1.5 !px-2.5 text-[11px] flex items-center gap-1 flex-1 justify-center"
                    style={{ color: 'var(--theme-primary)' }}
                    data-testid={`evt-emargement-${e.id}`}
                    aria-label="Émargement"
                  >
                    <ClipboardCheck className="w-3.5 h-3.5" /> Émargement
                  </button>
                  {e.billetterie && (
                    <>
                      <button
                        onClick={() => setStatsFor(e.id)}
                        className="btn-ghost !py-1.5 !px-2.5 text-[11px] flex items-center gap-1"
                        data-testid={`evt-stats-${e.id}`}
                        aria-label="Stats billetterie"
                        title="Stats billetterie"
                      >
                        <BarChart3 className="w-3.5 h-3.5" />
                      </button>
                      <a
                        href={`/billetterie?event_id=${e.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-ghost !py-1.5 !px-2.5 text-[11px] flex items-center gap-1"
                        data-testid={`evt-open-billetterie-${e.id}`}
                        title="Ouvrir la billetterie publique"
                      >
                        <Ticket className="w-3.5 h-3.5" /> <ExternalLink className="w-3 h-3" />
                      </a>
                    </>
                  )}
                  <button
                    onClick={() => setEditing(e)}
                    className="btn-ghost !py-1.5 !px-2.5 text-[11px] flex items-center gap-1"
                    data-testid={`evt-edit-${e.id}`}
                    aria-label="Modifier"
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(e)}
                    className="btn-ghost !py-1.5 !px-2.5 text-[11px] flex items-center gap-1 text-rose-600 hover:bg-rose-50"
                    data-testid={`evt-delete-${e.id}`}
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && <EventEditor evt={editing} onClose={() => { setEditing(null); load(); }} />}
      {emargement && <EmargementModal evenementId={emargement} onClose={() => { setEmargement(null); load(); }} />}
      {statsFor && <BilletterieStatsModal evenementId={statsFor} onClose={() => setStatsFor(null)} />}
    </div>
  );
}

function EventEditor({ evt, onClose }) {
  const isEdit = !!evt.id;
  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    nom: evt.nom || '',
    description: evt.description || '',
    type: evt.type || 'reunion',
    date_debut: toLocalInput(evt.date_debut),
    date_fin: toLocalInput(evt.date_fin),
    lieu: evt.lieu || '',
    capacite: evt.capacite || '',
    image_url: evt.image_url || '',
    billetterie: !!evt.billetterie,
    mode_spectacle: !!(evt.mode_spectacle || evt.plan_salle?.mode === 'spectacle'),
  });
  const [planSalle, setPlanSalle] = useState(evt.plan_salle || null);
  const [saving, setSaving] = useState(false);
  const [showPlanEditor, setShowPlanEditor] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nom: form.nom,
        description: form.description,
        type: form.type,
        date_debut: form.date_debut ? new Date(form.date_debut).toISOString() : null,
        date_fin: form.date_fin ? new Date(form.date_fin).toISOString() : null,
        lieu: form.lieu,
        capacite: form.capacite ? +form.capacite : null,
        image_url: form.image_url,
        billetterie: form.billetterie,
        mode_spectacle: form.mode_spectacle,
        plan_salle: form.billetterie ? (planSalle ? { ...planSalle, mode: form.mode_spectacle ? 'spectacle' : 'simple' } : null) : null,
      };
      if (isEdit) {
        await api.put(`/evenements/${evt.id}`, payload);
        toast.success('Événement modifié');
      } else {
        await api.post('/evenements', payload);
        toast.success('Événement créé');
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="evt-editor-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg sm:text-xl font-bold text-[#0F172A]">
            {isEdit ? 'Modifier l\'événement' : 'Créer un événement'}
          </h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Nom <span className="text-rose-500">*</span></label>
            <input required type="text" placeholder="Ex: Concert d'été 2026" className="input-base" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="evt-nom" />
          </div>

          <div>
            <label className="label-uppercase block mb-1.5">Description</label>
            <textarea placeholder="Détails, artistes, programme…" rows={3} className="input-base" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="evt-description" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Type</label>
              <select className="input-base" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="evt-type">
                <option value="reunion">Réunion</option>
                <option value="concert">Concert</option>
                <option value="sport">Sport</option>
                <option value="salon">Salon / Forum</option>
                <option value="formation">Formation</option>
                <option value="ag">Assemblée générale</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Capacité</label>
              <input type="number" min="0" placeholder="Ex: 200" className="input-base" value={form.capacite} onChange={(e) => setForm({ ...form, capacite: e.target.value })} data-testid="evt-capacite" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Début <span className="text-rose-500">*</span></label>
              <input required type="datetime-local" className="input-base" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} data-testid="evt-date-debut" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Fin</label>
              <input type="datetime-local" className="input-base" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} data-testid="evt-date-fin" />
            </div>
          </div>

          <div>
            <label className="label-uppercase block mb-1.5">Lieu</label>
            <input type="text" placeholder="Ex: Le Caveau Blues · Lyon 1er" className="input-base" value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} data-testid="evt-lieu" />
          </div>

          <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} label="Photo de l'événement (optionnel)" testid="evt-image" aspect="16/9" />

          <label className="flex items-center gap-3 p-3 bg-[#F9F9F8] border border-[#E2E8F0] rounded-lg cursor-pointer hover:bg-[#F1F5F9]">
            <input
              type="checkbox"
              checked={form.billetterie}
              onChange={(e) => setForm({ ...form, billetterie: e.target.checked })}
              className="w-4 h-4 accent-[var(--theme-primary)]"
              data-testid="evt-billetterie"
            />
            <div className="flex-1">
              <div className="text-sm font-bold text-[#0F172A]">Activer la billetterie</div>
              <div className="text-xs text-[#475569]">Génère une page publique avec QR codes + plan de salle</div>
            </div>
            <Ticket className="w-5 h-5 text-[#94A3B8]" />
          </label>

          {form.billetterie && (
            <label className="flex items-center gap-3 p-3 bg-[#F9F9F8] border border-[#E2E8F0] rounded-lg cursor-pointer hover:bg-[#F1F5F9]">
              <input
                type="checkbox"
                checked={form.mode_spectacle}
                onChange={(e) => setForm({ ...form, mode_spectacle: e.target.checked })}
                className="w-4 h-4 accent-[var(--theme-primary)]"
                data-testid="evt-mode-spectacle"
              />
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0F172A]">Mode Spectacle</div>
                <div className="text-xs text-[#475569]">Active la sélection de sièges numérotés (sinon, billets libres)</div>
              </div>
              <Theater className="w-5 h-5 text-[#94A3B8]" />
            </label>
          )}

          {form.billetterie && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="plan-salle-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-amber-900">Plan de salle</div>
                  <div className="text-xs text-amber-700">
                    {planSalle && planSalle.zones?.length
                      ? `${planSalle.zones.length} zone${planSalle.zones.length > 1 ? 's' : ''} · ${planSalle.zones.reduce((acc, z) => acc + (z.standing ? (z.places || 0) : (z.rangees || []).reduce((a, r) => a + r.seats.filter((s) => s !== '|').length, 0)), 0)} places`
                      : 'Aucun plan défini — sans plan, la billetterie est désactivée'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPlanEditor(true)}
                  className="btn-secondary !py-1.5 !px-3 text-xs"
                  data-testid="open-plan-editor"
                >{planSalle ? 'Modifier le plan' : 'Configurer le plan'}</button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="evt-submit">
            {saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer')}
          </button>
        </div>
      </form>

      {showPlanEditor && (
        <PlanSalleEditor
          initial={planSalle}
          onSave={(p) => { setPlanSalle(p); setShowPlanEditor(false); }}
          onClose={() => setShowPlanEditor(false)}
        />
      )}
    </div>
  );
}
